import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

// ── Percent → color (matches light theme) ─────────────────────────────────────
const pctColor  = (p) => p >= 100 ? "#059669" : p >= 75 ? "#2563eb" : p >= 50 ? "#d97706" : "#dc2626";
const pctBg     = (p) => p >= 100 ? "#d1fae5" : p >= 75 ? "#dbeafe" : p >= 50 ? "#fef3c7" : "#fee2e2";
const pctLabel  = (p) => p >= 100 ? "مكتملة" : p >= 75 ? "متقدمة" : p >= 50 ? "متوسطة" : "تحتاج عمل";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

// ── Progress bar (light theme) ─────────────────────────────────────────────────
function ProgressBar({ pct, height = 8 }) {
  const clamped = Math.min(pct, 100);
  const col     = pctColor(pct);
  return (
    <div style={{ background: "rgba(0,0,0,0.07)", borderRadius: 999, height, overflow: "hidden" }}>
      <div style={{
        height: "100%", width: `${clamped}%`,
        background: col, borderRadius: 999,
        transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
      }} />
    </div>
  );
}

// ── Difficulty badge ───────────────────────────────────────────────────────────
function DBadge({ label, count, color, bg }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      background: bg, border: `1px solid ${color}30`,
      borderRadius: 6, padding: "2px 8px",
      fontSize: "0.72rem", fontWeight: 700, color,
    }}>
      {label}: {count}
    </span>
  );
}

// ── Summary KPI card ───────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color = "#2563eb" }) {
  return (
    <div className="admin-kpi">
      <div style={{ fontSize: "1.4rem", marginBottom: "0.2rem" }}>{icon}</div>
      <div className="admin-kpi-value" style={{ color }}>{value}</div>
      <div className="admin-kpi-label">{label}</div>
    </div>
  );
}

// ── Rank table ─────────────────────────────────────────────────────────────────
function RankTable({ title, icon, rows, limit = 6 }) {
  return (
    <div className="admin-card flex-1" style={{ minWidth: 0 }}>
      <div className="font-black text-primary mb-4 text-sm">{icon} {title}</div>
      {rows.length === 0 && <p className="text-primary/30 text-sm">لا توجد بيانات</p>}
      {rows.slice(0, limit).map((cat, i) => (
        <div key={cat.id} style={{
          display: "flex", alignItems: "center", gap: "0.5rem",
          marginBottom: "0.5rem", paddingBottom: "0.5rem",
          borderBottom: i < Math.min(rows.length, limit) - 1 ? "1px solid rgba(0,0,0,0.05)" : "none",
        }}>
          <span style={{ width: 20, textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
            {i + 1}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "rgba(0,0,0,0.75)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: "0.2rem" }}>
              {cat.name}
            </div>
            <ProgressBar pct={cat.pct} height={5} />
          </div>
          <span style={{ fontSize: "0.8rem", fontWeight: 900, color: pctColor(cat.pct), flexShrink: 0, minWidth: 60, textAlign: "left" }}>
            {cat.total}/{cat.goal}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Category Detail Modal ──────────────────────────────────────────────────────
function CategoryDetailModal({ cat, onClose, onEditGoal, onNavigate }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9900, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: "1.2rem", padding: "1.75rem", width: "100%", maxWidth: 460, fontFamily: "Cairo, sans-serif", direction: "rtl", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem" }}>
          <h3 style={{ color: "#5B0E14", fontWeight: 900, margin: 0, fontSize: "1.1rem" }}>📂 {cat.name}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "1.2rem", cursor: "pointer" }}>✕</button>
        </div>

        {/* Progress */}
        <div style={{ background: "rgba(0,0,0,0.04)", borderRadius: "0.75rem", padding: "1rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <span style={{ color: "rgba(0,0,0,0.5)", fontSize: "0.82rem" }}>التقدم</span>
            <span style={{ color: pctColor(cat.pct), fontWeight: 900, fontSize: "1rem" }}>{Math.round(cat.pct)}%</span>
          </div>
          <ProgressBar pct={cat.pct} height={10} />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", fontSize: "0.78rem", color: "rgba(0,0,0,0.4)" }}>
            <span>{cat.total} سؤال</span>
            <button onClick={() => { onClose(); onEditGoal(cat); }} style={{ background: "none", border: "none", color: "#5B0E14", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, textDecoration: "underline dotted" }}>
              الهدف: {cat.goal} ✏️
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "1rem" }}>
          {[
            { icon: "🟢", label: "300 نقطة",      value: cat.d300, color: "#059669" },
            { icon: "🟠", label: "600 نقطة",      value: cat.d600, color: "#d97706" },
            { icon: "🔴", label: "900 نقطة",      value: cat.d900, color: "#dc2626" },
            { icon: "🖼",  label: "أسئلة بصور",   value: cat.with_image },
            { icon: "🔊", label: "أسئلة صوتية",   value: cat.with_audio, color: "#7c3aed" },
            { icon: "🚩", label: "بلاغات مفتوحة", value: cat.reports, color: cat.reports > 0 ? "#dc2626" : undefined },
            { icon: "🎮", label: "مرات اللعب",     value: cat.total_plays?.toLocaleString("ar-SA") ?? 0 },
            { icon: "📅", label: "آخر إضافة",      value: fmtDate(cat.last_added) },
          ].map(({ icon, label, value, color }) => (
            <div key={label} style={{ background: "rgba(0,0,0,0.03)", borderRadius: "0.6rem", padding: "0.55rem 0.75rem", border: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.4)" }}>{icon} {label}</div>
              <div style={{ fontWeight: 900, color: color || "#5B0E14", fontSize: "1.05rem", marginTop: "0.1rem" }}>{value ?? 0}</div>
            </div>
          ))}
        </div>

        {/* Balance warning */}
        {cat.imbalanced && (
          <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: "0.7rem", padding: "0.7rem 0.9rem", marginBottom: "0.85rem" }}>
            <div style={{ fontWeight: 700, color: "#92400e", fontSize: "0.85rem" }}>⚠ توزيع غير متوازن</div>
            {cat.priority.length > 0 && (
              <div style={{ color: "#78350f", fontSize: "0.78rem", marginTop: "0.25rem" }}>
                يُفضَّل إضافة {cat.priority.map((p) => `${p} نقطة`).join("، ثم ")} أولاً
              </div>
            )}
          </div>
        )}

        {cat.imbalanced && cat.priority.length > 0 && (
          <button
            onClick={() => { onClose(); if (onNavigate) onNavigate("ai_assistant"); }}
            style={{ width: "100%", background: "#5B0E14", color: "#F1E194", border: "none", borderRadius: "0.7rem", padding: "0.65rem", fontSize: "0.88rem", fontWeight: 900, cursor: "pointer" }}
          >
            ✨ اقترح إنشاء أسئلة {cat.priority[0]} نقطة عبر AI
          </button>
        )}
      </div>
    </div>
  );
}

// ── Goal Edit Modal ────────────────────────────────────────────────────────────
function GoalEditModal({ cat, onSave, onClose }) {
  const [value, setValue] = useState(String(cat.goal));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 1) return;
    setSaving(true);
    await onSave(cat.id, n);
    setSaving(false);
    onClose();
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: "1rem", padding: "1.5rem", width: 320, fontFamily: "Cairo, sans-serif", direction: "rtl", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
      >
        <h3 style={{ color: "#5B0E14", fontWeight: 900, margin: "0 0 0.25rem", fontSize: "1rem" }}>🎯 تعديل هدف الفئة</h3>
        <p style={{ color: "rgba(0,0,0,0.4)", fontSize: "0.82rem", margin: "0 0 0.85rem" }}>{cat.name}</p>
        <input
          type="number" min="1" value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          autoFocus
          style={{
            width: "100%", padding: "0.65rem", border: "2px solid rgba(91,14,20,0.25)", borderRadius: "0.6rem",
            color: "#5B0E14", fontSize: "1.1rem", fontWeight: 900, outline: "none",
            textAlign: "center", boxSizing: "border-box", background: "#fdf6e3",
          }}
        />
        <p style={{ color: "rgba(0,0,0,0.35)", fontSize: "0.72rem", margin: "0.4rem 0 0.85rem" }}>الهدف الحالي: {cat.goal} سؤال</p>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: "#5B0E14", color: "#F1E194", border: "none", borderRadius: "0.6rem", padding: "0.6rem", fontWeight: 900, fontSize: "0.9rem", cursor: "pointer", opacity: saving ? 0.7 : 1 }}
          >
            {saving ? "⏳" : "✓ حفظ"}
          </button>
          <button
            onClick={onClose}
            style={{ flex: 1, background: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.5)", border: "1px solid rgba(0,0,0,0.1)", borderRadius: "0.6rem", padding: "0.6rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Category Card ──────────────────────────────────────────────────────────────
function CategoryCard({ cat, onDetail, onEditGoal }) {
  const pct = Math.round(cat.pct);
  const col  = pctColor(pct);
  const bg   = pctBg(pct);

  return (
    <div
      className="admin-card"
      onClick={() => onDetail(cat)}
      style={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: "0.65rem" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
    >
      {/* Name + Status badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
        <span style={{ fontWeight: 900, fontSize: "0.88rem", color: "#5B0E14", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {cat.name}
        </span>
        <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: bg, color: col, flexShrink: 0 }}>
          {pctLabel(pct)}
        </span>
      </div>

      {/* Percent + progress */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.3rem" }}>
          <span style={{ fontSize: "1.5rem", fontWeight: 900, color: col, lineHeight: 1 }}>{pct}%</span>
          <span style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.4)" }}>
            {cat.total}&nbsp;/&nbsp;
            <button
              onClick={(e) => { e.stopPropagation(); onEditGoal(cat); }}
              style={{ background: "none", border: "none", color: "#5B0E14", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, padding: 0, textDecoration: "underline dotted" }}
              title="تعديل الهدف"
            >
              {cat.goal}
            </button>
          </span>
        </div>
        <ProgressBar pct={pct} />
      </div>

      {/* Difficulty badges */}
      <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
        <DBadge label="300" count={cat.d300} color="#059669" bg="#d1fae5" />
        <DBadge label="600" count={cat.d600} color="#d97706" bg="#fef3c7" />
        <DBadge label="900" count={cat.d900} color="#dc2626" bg="#fee2e2" />
      </div>

      {/* Balance warning */}
      {cat.imbalanced && (
        <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "0.5rem", padding: "0.35rem 0.6rem", fontSize: "0.7rem", color: "#92400e" }}>
          ⚠ توزيع غير متوازن
          {cat.priority.length > 0 && <span style={{ color: "#78350f", marginRight: "0.3rem" }}>— أضف {cat.priority[0]} نقطة أولاً</span>}
        </div>
      )}

      {/* Footer chips */}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", fontSize: "0.68rem", color: "rgba(0,0,0,0.4)", marginTop: "auto" }}>
        {cat.with_image > 0 && <span>🖼 {cat.with_image}</span>}
        {cat.with_audio > 0 && <span>🔊 {cat.with_audio}</span>}
        {cat.reports    > 0 && <span style={{ color: "#dc2626" }}>🚩 {cat.reports}</span>}
        {cat.total_plays > 0 && <span>🎮 {cat.total_plays.toLocaleString("ar")}</span>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN PANEL
// ══════════════════════════════════════════════════════════════════════════════
export default function ContentProgressPanel({ token, onNavigate }) {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [filterMode,     setFilterMode]     = useState("all");
  const [sortBy,         setSortBy]         = useState("pct_desc");
  const [detailCat,      setDetailCat]      = useState(null);
  const [editingGoalCat, setEditingGoalCat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API}/admin/content-progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
    } catch {
      setError("فشل تحميل البيانات — تحقق من اتصالك وأعد المحاولة");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const saveGoal = async (catId, newGoal) => {
    try {
      await axios.put(`${API}/admin/content-progress/goals`, { [catId]: newGoal }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((prev) => ({
        ...prev,
        categories: prev.categories.map((c) =>
          c.id === catId ? { ...c, goal: newGoal, pct: (c.total / newGoal) * 100 } : c
        ),
      }));
    } catch { /* silent */ }
  };

  // ── Loading / Error ──────────────────────────────────────────────────────────
  if (loading) {
    return <div className="p-6 text-center text-primary/30 py-20">⏳ جاري تحميل بيانات المحتوى...</div>;
  }
  if (error) {
    return (
      <div className="p-6 text-center py-20">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={load} className="bg-primary text-secondary px-6 py-2 rounded-xl font-black text-sm hover:opacity-90 transition-opacity">
          إعادة المحاولة
        </button>
      </div>
    );
  }
  if (!data) return null;

  const { summary, categories } = data;

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const sorted = [...categories].sort((a, b) => {
    if (sortBy === "pct_desc")   return b.pct         - a.pct;
    if (sortBy === "pct_asc")    return a.pct         - b.pct;
    if (sortBy === "plays_desc") return b.total_plays - a.total_plays;
    if (sortBy === "total_desc") return b.total       - a.total;
    if (sortBy === "name")       return (a.name || "").localeCompare(b.name || "", "ar");
    return 0;
  });

  // ── Filter ───────────────────────────────────────────────────────────────────
  const filtered = sorted.filter((c) => {
    if (filterMode === "incomplete") return c.pct < 100;
    if (filterMode === "imbalanced") return c.imbalanced;
    if (filterMode === "complete")   return c.pct >= 100;
    return true;
  });

  // ── Rank lists ───────────────────────────────────────────────────────────────
  const incomplete   = categories.filter((c) => c.pct < 100);
  const nearComplete = [...incomplete].sort((a, b) => b.pct - a.pct);
  const needsHelp    = [...incomplete].sort((a, b) => a.pct - b.pct);
  const mostPlayed   = [...categories].filter((c) => c.total_plays > 0).sort((a, b) => b.total_plays - a.total_plays);
  const leastPlayed  = [...categories].filter((c) => c.total > 0).sort((a, b) => a.total_plays - b.total_plays);

  const filterTabs = [
    { key: "all",        label: "الكل",       count: categories.length },
    { key: "incomplete", label: "ناقصة",       count: categories.filter((c) => c.pct < 100).length },
    { key: "imbalanced", label: "غير متوازنة", count: categories.filter((c) => c.imbalanced).length },
    { key: "complete",   label: "مكتملة ✓",    count: categories.filter((c) => c.pct >= 100).length },
  ];

  return (
    <div className="p-6" dir="rtl">

      {/* ══ Header ════════════════════════════════════════════════════════════ */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black">📊 تقدم المحتوى</h2>
          <p className="text-primary/50 text-sm mt-1">اكتمال الفئات · توازن الصعوبة · إحصائيات اللعب</p>
        </div>
        <button
          onClick={load}
          className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold hover:bg-primary/20 transition-all"
        >
          ↻ تحديث
        </button>
      </div>

      {/* ══ Summary KPIs ══════════════════════════════════════════════════════ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <KpiCard icon="📚" label="إجمالي الفئات"  value={summary.total_categories}                        color="#5B0E14" />
        <KpiCard icon="📄" label="إجمالي الأسئلة" value={summary.total_questions.toLocaleString("ar-SA")} color="#2563eb" />
        <KpiCard icon="📈" label="متوسط / فئة"    value={summary.avg_per_category}                        color="#7c3aed" />
        <KpiCard icon="🏁" label="مكتملة"          value={summary.completed}                               color="#059669" />
        <KpiCard icon="🚧" label="ناقصة"           value={summary.incomplete}                              color="#d97706" />
      </div>

      {/* ══ Rank Tables ═══════════════════════════════════════════════════════ */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <RankTable icon="🏁" title="الأقرب للاكتمال"  rows={nearComplete} />
        <RankTable icon="🚧" title="الأكثر احتياجاً"  rows={needsHelp}    />
      </div>

      {/* ══ Filter + Sort bar ═════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          {filterTabs.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterMode(f.key)}
              className={`text-sm px-3 py-1 rounded-lg font-bold tracking-wide transition-all ${
                filterMode === f.key ? "bg-secondary text-primary" : "text-primary/50 hover:text-primary"
              }`}
            >
              {f.label} <span className="opacity-60">({f.count})</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-primary/40">ترتيب:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-sm border border-primary/15 rounded-lg px-2 py-1 bg-white text-primary font-bold outline-none cursor-pointer"
          >
            <option value="pct_desc">نسبة الإنجاز ↓</option>
            <option value="pct_asc">نسبة الإنجاز ↑</option>
            <option value="total_desc">إجمالي الأسئلة</option>
            <option value="plays_desc">الأكثر لعباً</option>
            <option value="name">الاسم أ→ي</option>
          </select>
        </div>
      </div>

      {/* ══ Category Grid ═════════════════════════════════════════════════════ */}
      {filtered.length === 0 ? (
        <div className="admin-card text-center text-primary/30 py-10 mb-6">لا توجد فئات تطابق الفلتر الحالي</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "0.75rem", marginBottom: "1.75rem" }}>
          {filtered.map((cat) => (
            <CategoryCard key={cat.id} cat={cat} onDetail={setDetailCat} onEditGoal={setEditingGoalCat} />
          ))}
        </div>
      )}

      {/* ══ Most / Least Played ═══════════════════════════════════════════════ */}
      <div className="admin-card mb-2">
        <div className="font-black text-primary mb-4">🎮 إحصائيات اللعب — بعد إطلاق اللعبة</div>
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>

          {/* Most played */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="font-bold text-primary/60 text-xs mb-3 uppercase tracking-wider">🔥 أكثر الفئات لعباً</div>
            {mostPlayed.length === 0 ? (
              <p className="text-primary/30 text-sm">لا توجد بيانات لعب بعد</p>
            ) : mostPlayed.slice(0, 6).map((cat, i) => (
              <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem", paddingBottom: "0.4rem", borderBottom: i < 5 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <span style={{ width: 20, textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: "0.75rem", fontWeight: 700 }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: "0.82rem", color: "rgba(0,0,0,0.7)", fontWeight: 600 }}>{cat.name}</span>
                <span style={{ fontSize: "0.8rem", fontWeight: 900, color: "#d97706" }}>🎮 {cat.total_plays.toLocaleString("ar-SA")}</span>
              </div>
            ))}
          </div>

          <div style={{ width: 1, background: "rgba(0,0,0,0.07)", flexShrink: 0 }} />

          {/* Least played */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="font-bold text-primary/60 text-xs mb-3 uppercase tracking-wider">📉 أقل الفئات لعباً</div>
            {leastPlayed.length === 0 ? (
              <p className="text-primary/30 text-sm">لا توجد بيانات لعب بعد</p>
            ) : leastPlayed.slice(0, 6).map((cat, i) => (
              <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem", paddingBottom: "0.4rem", borderBottom: i < 5 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                <span style={{ width: 20, textAlign: "center", color: "rgba(0,0,0,0.3)", fontSize: "0.75rem", fontWeight: 700 }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: "0.82rem", color: "rgba(0,0,0,0.7)", fontWeight: 600 }}>{cat.name}</span>
                <span style={{ fontSize: "0.8rem", fontWeight: 900, color: "#2563eb" }}>🎮 {cat.total_plays.toLocaleString("ar-SA")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ Modals ════════════════════════════════════════════════════════════ */}
      {detailCat && (
        <CategoryDetailModal
          cat={detailCat}
          onClose={() => setDetailCat(null)}
          onEditGoal={(cat) => { setDetailCat(null); setEditingGoalCat(cat); }}
          onNavigate={onNavigate}
        />
      )}
      {editingGoalCat && (
        <GoalEditModal cat={editingGoalCat} onSave={saveGoal} onClose={() => setEditingGoalCat(null)} />
      )}
    </div>
  );
}
